import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import { AccessDeniedState, LoadingState } from './ui/PageState'

/**
 * Limits a page to platform super-admins, who are the only ones the backend lets through.
 *
 * The action-history audit query has no organization column to scope against (see
 * AuditLogController.getActionHistory) and the product feedback queue spans every workspace, so the
 * backend restricts both to super_admin. Gate the route the same way instead of letting org admins hit
 * a 403 on load.
 */
export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, hasRole } = useAuth()
  const { t } = useTranslation()

  if (isLoading) {
    return <LoadingState title={t('states.loadingPageTitle')} description={t('states.loadingWorkspaceDescription')} />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!hasRole('super_admin')) {
    return (
      <AccessDeniedState
        title={t('states.superAdminAccessTitle')}
        description={t('states.superAdminAccessDescription')}
        requiredRole="SUPER ADMIN"
      />
    )
  }

  return <>{children}</>
}
